import PropTypes from 'prop-types'
import React from 'react'
import { findDOMNode } from 'react-dom'
import { DropTarget } from 'react-dnd'
import { injectIntl, FormattedMessage } from 'react-intl'
import { PermissionLevels } from 'utils/permission'
import {
  ProgressBar,
  Button,
  ButtonGroup
} from 'react-bootstrap'
import KanBoIcon from 'components/KanBoIcon'
import KanBoPicker from 'components/KanBoPicker'
import {
  ToggleModal
} from 'components/Modal'
import {
  ContentBody
} from 'components/Content'
import Sortable from 'components/Sortable'
import ElementInfo from '../ElementInfo'
import checklistItemDragType from '../Checklist/ChecklistItem/dragType'
import SubCard from './SubCard'
import AddSubCard from './AddSubCard'
import Element from '../Element'
import classes from './SubCardGroup.module.scss'
import { withBoardPermission } from 'dataProviders/Board'
import TemplateField from 'components/CardTemplateField'

const draggableOverStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: '#ccc',
  opacity: 0.8,
  zIndex: 2
}

export class SubCardGroup extends React.Component {
  static propTypes = {
    intl: PropTypes.object.isRequired,
    connectChecklistItemTarget: PropTypes.func.isRequired,
    isChecklistItemOver: PropTypes.bool.isRequired,
    canDropChecklistItem: PropTypes.bool.isRequired,

    permission: PropTypes.object.isRequired,

    isCardArchived: PropTypes.bool.isRequired,
    items: PropTypes.array.isRequired,
    isBusy: PropTypes.bool,
    boardId: PropTypes.number.isRequired,
    statusId: PropTypes.number.isRequired,
    cardId: PropTypes.number.isRequired,
    isTemplate: PropTypes.bool.isRequired,
    childCount: PropTypes.number.isRequired,
    completedChildCount: PropTypes.number.isRequired,

    requestPositionSubCard: PropTypes.func.isRequired,
    requestConvertChecklistItemToSubCard: PropTypes.func.isRequired,

    id: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string
    ]),
    sortable: PropTypes.object.isRequired
  }

  state = {
    convertingChecklistItem: null,
    convertBoardId: this.props.boardId,
    convertStatusId: null,
    convertTemplate: null,
  }

  hideConvertChecklistItem = () => {
    this.setState({
      convertingChecklistItem: null,
      convertBoardId: this.props.boardId,
      convertStatusId: null,
      convertTemplate: null,
    })
  }

  handleModalShowToggle = (show) => {
    if (!show) {
      this.hideConvertChecklistItem()
    }
  }

  handleConvertChecklistItemClick = () => {
    const {
      id,
      requestConvertChecklistItemToSubCard
    } = this.props
    const {
      convertingChecklistItem,
      convertStatusId,
      convertTemplate,
    } = this.state

    requestConvertChecklistItemToSubCard({
      checklistItemId: convertingChecklistItem.id,
      checklistId: convertingChecklistItem.parentId,
      statusId: convertStatusId,
      template: (convertTemplate && convertTemplate !== 'empty') ? { type: 'card', id: +convertTemplate } : 'empty',
      groupTarget: {
        groupId: id
      }
    })

    this.hideConvertChecklistItem()
  }

  handleBoardChange = (convertBoardId) => {
    this.setState({
      convertBoardId,
      convertTemplate: null,
    })
  }

  handleStatusChange = (convertStatusId) => {
    this.setState({
      convertStatusId
    })
  }

  handleTemplateChange = (template) => {
    this.setState({
      convertTemplate: template
    })
  }

  handleDocumentListItemPositionChange = ({ itemId, parentId, position }) => {
    this.props.requestPositionSubCard({
      cardId: this.props.cardId,
      subCardId: itemId,
      subCardGroupId: parentId,
      position
    })
  }

  renderSubCard = React.forwardRef((props, ref) =>
    <SubCard {...props} ref={ref} subCardGroupId={this.props.id} />
  )

  render () {
    const {
      connectChecklistItemTarget,
      isChecklistItemOver,
      canDropChecklistItem,

      permission,
      isCardArchived,
      isTemplate,
      items,
      isBusy,
      boardId,
      statusId,
      childCount,
      completedChildCount,

      id,
      sortable
    } = this.props
    const {
      convertingChecklistItem,
      convertBoardId,
      convertStatusId,
      convertTemplate,
    } = this.state

    let completedPercentage = 0
    if (childCount > 0 && completedChildCount > 0) {
      completedPercentage = Math.floor(completedChildCount / childCount * 100)
    }

    let progressBarBsStyle = null
    if (completedPercentage === 100) {
      progressBarBsStyle = 'success'
    }

    return (
      <Sortable
        vertical
        className={classes.cardContainer}
        name='subCards'
        id={id}
        component={this.renderSubCard}
        items={items}
        disabled={isBusy || isCardArchived || !permission.member}
        onPositionChange={this.handleDocumentListItemPositionChange}
      >
        {({ renderContainer, connectDropTarget }) =>
          <ToggleModal
            backdrop
            show={!!convertingChecklistItem}
            attachClickHandler={false}
            attachment='top center'
            targetAttachment='top center'
            constraints={[
              {
                to: 'window',
                pin: true
              }
            ]}
            onToggle={this.handleModalShowToggle}
          >
            <Element
              ref={ref => {
                const node = !isBusy ? findDOMNode(ref) : null

                connectDropTarget(node)

                connectChecklistItemTarget(canDropChecklistItem ? node : null)
              }}
              id={id}
              icon={<KanBoIcon name='kanbo-24-relation-child' />}
              collapsible
              sortable={sortable}
            >
              {(collapsed) => (
                <React.Fragment>
                  {isChecklistItemOver && <div style={draggableOverStyle} />}
                  {childCount > 0 &&
                    <ContentBody
                      className={classes.progressBody}
                    >
                      <ProgressBar
                        style={{
                          height: 10,
                          margin: '0 10px'
                        }}
                        className={classes.contentChild}
                        now={completedPercentage}
                        bsStyle={progressBarBsStyle}
                      />
                    </ContentBody>
                  }
                  <ContentBody
                    className={!collapsed ? classes.body : null}
                  >
                    {collapsed &&
                      <React.Fragment>
                        {childCount > 0 &&
                          <ElementInfo
                            icon={<KanBoIcon name='kanbo-16-tasks' />}
                          >
                            {completedChildCount}{'/'}{childCount}
                          </ElementInfo>
                        }
                        <ElementInfo
                          icon={<KanBoIcon name='kanbo-16-card' />}
                        >
                          {items.length}
                        </ElementInfo>
                      </React.Fragment>
                    }
                    {!collapsed && renderContainer()}
                  </ContentBody>
                  {(!collapsed && !isCardArchived && !isTemplate && permission.member) && (
                    <AddSubCard
                      id={id}
                    />
                  )}
                </React.Fragment>
              )}
            </Element>
            <div className={classes.modal}>
              <KanBoPicker
                board
                status
                currentBoardId={boardId}
                currentStatusId={statusId}
                requiredPermissionLevel={PermissionLevels.MEMBER}
                onBoardChange={this.handleBoardChange}
                onStatusChange={this.handleStatusChange}
              />
              <TemplateField
                boardId={convertBoardId}
                value={convertTemplate}
                onChange={this.handleTemplateChange}
              />
              <ButtonGroup className='m-b-10'>
                <Button
                  bsStyle='primary'
                  disabled={!convertStatusId}
                  onClick={this.handleConvertChecklistItemClick}
                >
                  <FormattedMessage id='CHECKLISTITEM_CONVERT_TO_SUBCARD' />
                </Button>
                <Button
                  onClick={this.hideConvertChecklistItem}
                >
                  <FormattedMessage id='CANCEL' />
                </Button>
              </ButtonGroup>
            </div>
          </ToggleModal>
        }
      </Sortable>
    )
  }
}

const checklistItemDropTarget = {
  canDrop (props, monitor) {
    const { permission, isTemplate } = props

    return permission.member && !isTemplate
  },

  drop (props, monitor, component) {
    const {
      boardId,
      statusId,
    } = props
    const {
      itemId,
      parentId
    } = monitor.getItem()

    setTimeout(() =>
      component.setState({
        convertingChecklistItem: {
          id: itemId,
          parentId
        },
        convertBoardId: boardId,
        convertStatusId: statusId,
        convertTemplate: null,
      })
    )
  }
}

export default
injectIntl(withBoardPermission(
  DropTarget(checklistItemDragType, checklistItemDropTarget, (connect, monitor) => ({
    connectChecklistItemTarget: connect.dropTarget(),
    isChecklistItemOver: monitor.isOver(),
    canDropChecklistItem: monitor.canDrop()
  }))(SubCardGroup)
), { forwardRef: true })
